import {
	chakra,
	Link as ChakraLink,
	LinkBox,
	LinkOverlay,
	useBreakpointValue,
	useMultiStyleConfig,
} from "@chakra-ui/react";
import { BlogListItemFragment } from "src/__generated__/cms-schema.codegen";
import { CMSButtonLabel } from "src/components/CMSButton/CMSButtonLabel";
import { DatoAsset } from "src/components/Dato/DatoAsset";
import { HeadingLevelBoundary, Hx } from "src/components/Heading/Heading";
import { NextLink } from "src/components/NextLink";
import { useTranslatedString } from "src/i18n/hooks";
import { DateDisplay } from "../DateDisplay/DateDisplay";

export type BlogpostTeaserProps = {
	blogpost: BlogListItemFragment;
};

export const BlogpostTeaser: React.FC<BlogpostTeaserProps> = ({ blogpost }) => {
	const styles = useMultiStyleConfig("Blogpost", { breakpoint: "md" });
	const t = useTranslatedString();

	const isMobile = useBreakpointValue(
		{
			base: true,
			md: false,
		},
		{ fallback: "base" },
	);

	const {
		id,
		slug,
		title,
		date,
		productFamilies,
		teaserText,
		teaserImage,
	} = blogpost;

	return (
		<LinkBox as="article" key={id} sx={styles.previewWrapper}>
			<chakra.div __css={styles.previewImageWrapper}>
				<DatoAsset
					data={teaserImage}
					datoImageProps={{
						objectFit: "cover",
						layout: "fill",
					}}
				/>
			</chakra.div>
			<chakra.div __css={styles.previewTextWrapper}>
				{!isMobile && (
					<chakra.div __css={styles.previewMetadata}>
						<chakra.span flexGrow={1}>
							{productFamilies.map((family) => family.name).join(", ")}
						</chakra.span>
						<DateDisplay date={date} />
					</chakra.div>
				)}
				<NextLink href={`${t("blog.urlSlug")}/${slug}`} passHref>
					<LinkOverlay>
						<HeadingLevelBoundary>
							<Hx
								sx={styles.previewTitle}
								pt={isMobile ? 4 : undefined}
							>
								{title}
							</Hx>
						</HeadingLevelBoundary>
					</LinkOverlay>
				</NextLink>
				<chakra.div px={3} flexGrow={1}>
					{teaserText}
				</chakra.div>
				<NextLink href={`${t("blog.urlSlug")}/${slug}`} passHref>
					<ChakraLink id={id} sx={styles.link}>
						<CMSButtonLabel label={t("blogpost.readMore")} />
					</ChakraLink>
				</NextLink>
			</chakra.div>
		</LinkBox>
	);
};

// 🔬 e2e: story
